import { createFileRoute, Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import {
  Mail,
  MessageSquare,
  Phone,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  CalendarPlus,
  Check,
  Clock,
} from 'lucide-react';
import { Sidebar } from '@/components/layout/sidebar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ComposeDialog } from '@/components/comms/compose-dialog';
import { EditableField } from '@/components/contact/editable-field';
import { CustomFieldsBlock } from '@/components/contact/custom-fields-block';
import { NotesTimeline } from '@/components/contact/notes-timeline';
import { ScheduleFollowUp } from '@/components/follow-ups/schedule-follow-up';
import {
  useFollowUps,
  useUpdateFollowUp,
  useSnoozeFollowUp,
} from '@/features/follow-ups/queries';
import { useContactDetails, useUpdateContact } from '@/features/contacts/queries';
import { useCurrentPipeline } from '@/features/preferences/use-pipeline';
import { useListCursor } from '@/hooks/use-list-cursor';
import { supabase } from '@/lib/supabase';
import { formatMoney, getInitials } from '@/lib/format';
import { cn } from '@/lib/utils';

interface ListCursorRouterState {
  listCursor?: {
    ids: string[];
    cursor: number;
    returnTo?: string;
  };
}

export const Route = createFileRoute('/_auth/contacts_/$id')({ component: ContactDetailPage });

interface DealRow {
  id: string;
  stage_name: string | null;
  deal_value: number | null;
  stage_probability: number | null;
  status: string | null;
  updated_at: string | null;
}
interface ActivityRow {
  id: string;
  type: string | null;
  body: string | null;
  created_at: string | null;
  agent_name?: string | null;
}

function ContactDetailPage(): JSX.Element {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const routerLocationState = useRouterState({
    select: (s) => s.location.state as ListCursorRouterState | undefined,
  });
  const listCursorState = routerLocationState?.listCursor ?? null;

  const cursor = useListCursor({
    ids: listCursorState?.ids ?? null,
    cursor: listCursorState?.cursor ?? null,
    onMove: (nextCursor, nextId) => {
      if (!listCursorState) return;
      void navigate({
        to: '/contacts/$id',
        params: { id: nextId },
        state: {
          listCursor: {
            ids: listCursorState.ids,
            cursor: nextCursor,
            ...(listCursorState.returnTo ? { returnTo: listCursorState.returnTo } : {}),
          },
        },
        replace: true,
      });
    },
  });

  const { data: contact, isLoading } = useContactDetails(id);
  const updateContact = useUpdateContact();
  const { pipeline } = useCurrentPipeline();
  const [deals, setDeals] = useState<DealRow[]>([]);
  const [activities, setActivities] = useState<ActivityRow[]>([]);
  const [composeChannel, setComposeChannel] = useState<'email' | 'sms' | null>(null);
  const [showMore, setShowMore] = useState(false);
  const [followUpOpen, setFollowUpOpen] = useState(false);

  const { data: followUpsData } = useFollowUps('all');
  const updateFollowUp = useUpdateFollowUp();
  const snoozeFollowUp = useSnoozeFollowUp();
  const contactFollowUps = useMemo(() => {
    const all = followUpsData?.follow_ups ?? [];
    return all
      .filter((f) => f.contact_id === id)
      .sort((a, b) => new Date(a.due_at).getTime() - new Date(b.due_at).getTime());
  }, [followUpsData?.follow_ups, id]);
  const openFollowUps = contactFollowUps.filter(
    (f) => f.status === 'pending' || f.status === 'snoozed',
  );
  const pastFollowUps = contactFollowUps.filter(
    (f) => f.status === 'done' || f.status === 'dismissed',
  );

  useEffect(() => {
    if (!id) return;
    void (async () => {
      const [{ data: dealRows }, { data: actRows }] = await Promise.all([
        supabase
          .from('opportunities_enriched')
          .select('id, stage_name, deal_value, stage_probability, status, updated_at')
          .eq('contact_id', id)
          .order('updated_at', { ascending: false }),
        supabase
          .from('activities')
          .select('id, type, body, created_at')
          .eq('contact_id', id)
          .order('created_at', { ascending: false })
          .limit(50),
      ]);
      setDeals((dealRows ?? []) as unknown as DealRow[]);
      setActivities((actRows ?? []) as unknown as ActivityRow[]);
    })();
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex h-screen bg-bg text-text">
        <Sidebar />
        <main className="flex-1 p-6 text-text-mute">Loading…</main>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="flex h-screen bg-bg text-text">
        <Sidebar />
        <main className="flex-1 p-6">
          <p className="text-text font-medium">This contact isn't available to you</p>
          <p className="text-text-mute text-sm mt-1 max-w-md">
            It may belong to another agent, or it may have been moved or removed. If you think
            you should have access, ask an admin to reassign it to you.
          </p>
          <Link to="/contacts" className="text-accent text-sm mt-3 inline-block">
            ← Back to Contacts
          </Link>
        </main>
      </div>
    );
  }

  const fullName = [contact.first_name, contact.last_name].filter(Boolean).join(' ') || '—';

  return (
    <div className="flex h-screen bg-bg text-text">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <div className="px-6 py-4 border-b border-border/[0.08] flex items-center gap-3">
          <Link
            to={(listCursorState?.returnTo as '/contacts' | undefined) ?? '/contacts'}
            className="text-accent text-sm hover:underline"
          >
            ← Back to Contacts
          </Link>
          {cursor.position && (
            <div className="flex items-center gap-1 ml-auto">
              <button
                type="button"
                onClick={() => cursor.prev?.()}
                disabled={!cursor.hasPrev}
                className="flex items-center gap-1 rounded border border-border/[0.16] bg-surface-2 px-2 py-1 text-[12px] text-text-dim hover:border-accent hover:text-text disabled:cursor-not-allowed disabled:opacity-40"
                title="Previous contact (↑)"
                aria-label="Previous contact"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Prev
              </button>
              <span className="px-2 font-mono text-[11px] text-text-mute">
                {cursor.position.current} of {cursor.position.total}
              </span>
              <button
                type="button"
                onClick={() => cursor.next?.()}
                disabled={!cursor.hasNext}
                className="flex items-center gap-1 rounded border border-border/[0.16] bg-surface-2 px-2 py-1 text-[12px] text-text-dim hover:border-accent hover:text-text disabled:cursor-not-allowed disabled:opacity-40"
                title="Next contact (↓)"
                aria-label="Next contact"
              >
                Next
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>

        <div className="px-6 py-5">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-14 w-14 rounded-full bg-accent/20 text-accent flex items-center justify-center font-display text-lg">
              {getInitials(fullName)}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-display text-2xl">{fullName}</h1>
              <div className="text-text-dim text-sm">
                {contact.company_name ?? '—'} · {contact.vertical ?? '—'} · {contact.city ?? '—'}
              </div>
            </div>
            <div className="flex gap-2">
              {contact.email && (
                <Button size="sm" variant="outline" onClick={() => setComposeChannel('email')}>
                  <Mail className="h-3.5 w-3.5 mr-1" /> Send Email
                </Button>
              )}
              {contact.phone && (
                <>
                  <Button size="sm" variant="outline" onClick={() => setComposeChannel('sms')}>
                    <MessageSquare className="h-3.5 w-3.5 mr-1" /> Send SMS
                  </Button>
                  <Button size="sm" asChild>
                    <Link
                      to="/dialler"
                      search={{ contactId: contact.id, autoDial: true }}
                      title={`Open dialler and ring ${contact.phone}`}
                    >
                      <Phone className="h-3.5 w-3.5 mr-1" /> Call
                    </Link>
                  </Button>
                </>
              )}
              <Button size="sm" variant="outline" onClick={() => setFollowUpOpen(true)}>
                <CalendarPlus className="h-3.5 w-3.5 mr-1" /> Follow-up
              </Button>
            </div>
          </div>

          {composeChannel && (
            <ComposeDialog
              open={!!composeChannel}
              onOpenChange={(o) => !o && setComposeChannel(null)}
              channel={composeChannel}
              contact={{
                id: contact.id,
                first_name: contact.first_name ?? null,
                last_name: contact.last_name ?? null,
                company_name: contact.company_name ?? null,
                email: contact.email ?? null,
                phone: contact.phone ?? null,
              }}
              pipeline={pipeline}
            />
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="bg-surface border-border/[0.16]">
              <CardHeader>
                <CardTitle className="text-sm">Contact details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <EditableField
                    label="First name"
                    value={contact.first_name ?? ''}
                    onSave={(v) =>
                      updateContact.mutateAsync({
                        id: contact.id,
                        patch: { first_name: v == null ? null : String(v) },
                      })
                    }
                  />
                  <EditableField
                    label="Last name"
                    value={contact.last_name ?? ''}
                    onSave={(v) =>
                      updateContact.mutateAsync({
                        id: contact.id,
                        patch: { last_name: v == null ? null : String(v) },
                      })
                    }
                  />
                  <EditableField
                    label="Phone"
                    type="tel"
                    mono
                    value={contact.phone ?? ''}
                    onSave={(v) =>
                      updateContact.mutateAsync({
                        id: contact.id,
                        patch: { phone: v == null ? null : String(v) },
                      })
                    }
                  />
                  <EditableField
                    label="Email"
                    type="email"
                    value={contact.email ?? ''}
                    onSave={(v) =>
                      updateContact.mutateAsync({
                        id: contact.id,
                        patch: { email: v == null ? null : String(v) },
                      })
                    }
                  />
                  <EditableField
                    label="Company"
                    value={contact.company_name ?? ''}
                    onSave={(v) =>
                      updateContact.mutateAsync({
                        id: contact.id,
                        patch: { company_name: v == null ? null : String(v) },
                      })
                    }
                  />
                  <EditableField
                    label="City"
                    value={contact.city ?? ''}
                    onSave={(v) =>
                      updateContact.mutateAsync({
                        id: contact.id,
                        patch: { city: v == null ? null : String(v) },
                      })
                    }
                  />
                  <EditableField
                    label="Website"
                    type="url"
                    link
                    value={contact.website ?? ''}
                    onSave={(v) =>
                      updateContact.mutateAsync({
                        id: contact.id,
                        patch: { website: v == null ? null : String(v) },
                      })
                    }
                  />
                  <EditableField
                    label="Turnover"
                    type="select"
                    options={[
                      { value: '<£85K', label: '< £85K' },
                      { value: '£85-150K', label: '£85–150K' },
                      { value: '£150-250K', label: '£150–250K' },
                      { value: '£250-500K', label: '£250–500K' },
                      { value: '£500K-1MM', label: '£500K–1MM' },
                      { value: '>£1MM', label: '> £1MM' },
                    ]}
                    value={contact.turnover ?? ''}
                    onSave={(v) =>
                      updateContact.mutateAsync({
                        id: contact.id,
                        patch: { turnover: v == null ? null : String(v) },
                      })
                    }
                  />
                </div>

                {/* Notes editable */}
                <div className="mt-4 rounded border border-border/[0.08] bg-surface-2 p-3">
                  <EditableField
                    label="Notes"
                    type="textarea"
                    placeholder="Anything important about this lead. Saves on blur."
                    value={contact.notes ?? ''}
                    onSave={(v) =>
                      updateContact.mutateAsync({
                        id: contact.id,
                        patch: { notes: v == null ? null : String(v) },
                      })
                    }
                  />
                </div>

                {/* Extra info expandable */}
                <div className="mt-4 rounded border border-border/[0.08] bg-surface-2 p-3">
                  <button
                    type="button"
                    onClick={() => setShowMore((v) => !v)}
                    className="mb-2 flex w-full items-center justify-between text-[10px] uppercase tracking-[0.12em] text-text-mute hover:text-text-dim"
                  >
                    <span className="inline-flex items-center gap-1">
                      {showMore ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                      {showMore ? 'Hide' : 'Show'} extra info
                    </span>
                    <span className="font-mono text-text-dim">
                      {Object.keys(contact.custom_fields ?? {}).length} field
                      {Object.keys(contact.custom_fields ?? {}).length === 1 ? '' : 's'}
                    </span>
                  </button>
                  {showMore && (
                    <CustomFieldsBlock
                      fields={contact.custom_fields ?? {}}
                      onSave={(next) =>
                        updateContact.mutateAsync({
                          id: contact.id,
                          patch: { custom_fields: next },
                        })
                      }
                    />
                  )}
                </div>

                {showMore && (
                  <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <EditableField
                      label="Address"
                      value={contact.address ?? ''}
                      onSave={(v) =>
                        updateContact.mutateAsync({
                          id: contact.id,
                          patch: { address: v == null ? null : String(v) },
                        })
                      }
                    />
                    <EditableField
                      label="Companies House #"
                      mono
                      value={contact.company_number ?? ''}
                      onSave={(v) =>
                        updateContact.mutateAsync({
                          id: contact.id,
                          patch: { company_number: v == null ? null : String(v) },
                        })
                      }
                    />
                    <EditableField
                      label="SIC code"
                      mono
                      value={contact.sic_code ?? ''}
                      onSave={(v) =>
                        updateContact.mutateAsync({
                          id: contact.id,
                          patch: { sic_code: v == null ? null : String(v) },
                        })
                      }
                    />
                    <EditableField
                      label="Years active"
                      type="number"
                      value={contact.years_active ?? ''}
                      onSave={(v) =>
                        updateContact.mutateAsync({
                          id: contact.id,
                          patch: { years_active: v == null ? null : Number(v) },
                        })
                      }
                    />
                    <EditableField
                      label="Do not call"
                      type="toggle"
                      value={contact.dnc ? 'true' : ''}
                      onSave={(v) =>
                        updateContact.mutateAsync({
                          id: contact.id,
                          patch: { dnc: !!v },
                        })
                      }
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-surface border-border/[0.16] lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-sm">Deals ({deals.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {deals.length === 0 ? (
                  <p className="text-text-mute text-sm">No deals yet.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-text-mute text-xs uppercase tracking-wider border-b border-border/[0.08]">
                        <th className="py-2">Stage</th>
                        <th className="py-2 text-right">Value</th>
                        <th className="py-2 text-right">Prob.</th>
                        <th className="py-2">Status</th>
                        <th className="py-2 text-right">Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deals.map((d) => (
                        <tr key={d.id} className="border-b border-border/[0.04]">
                          <td className="py-2">{d.stage_name ?? '—'}</td>
                          <td className="py-2 text-right font-mono">{formatMoney(d.deal_value ?? 0)}</td>
                          <td className="py-2 text-right font-mono">{d.stage_probability ?? 0}%</td>
                          <td className="py-2">
                            <span className="text-xs px-2 py-0.5 rounded bg-surface-2 border border-border/[0.08]">
                              {d.status ?? '—'}
                            </span>
                          </td>
                          <td className="py-2 text-right text-text-mute text-xs font-mono">
                            {d.updated_at ? new Date(d.updated_at).toLocaleDateString('en-GB') : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>

            <Card className="bg-surface border-border/[0.16] lg:col-span-3">
              <CardHeader>
                <CardTitle className="text-sm flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4 text-accent-2" />
                    Follow-ups ({openFollowUps.length} open
                    {pastFollowUps.length > 0 ? `, ${pastFollowUps.length} done` : ''})
                  </span>
                  <button
                    type="button"
                    onClick={() => setFollowUpOpen(true)}
                    className="rounded bg-accent px-2.5 py-1 text-[11px] font-semibold text-accent-fg hover:-translate-y-px transition"
                  >
                    + Schedule
                  </button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {openFollowUps.length === 0 ? (
                  <p className="text-text-mute text-sm">
                    No upcoming follow-ups for this contact.{' '}
                    <button
                      type="button"
                      onClick={() => setFollowUpOpen(true)}
                      className="text-accent hover:underline"
                    >
                      Schedule one →
                    </button>
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {openFollowUps.map((f) => {
                      const due = new Date(f.due_at);
                      const overdue = due.getTime() < Date.now();
                      const dueLabel = due.toLocaleString('en-GB', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      });
                      return (
                        <li
                          key={f.id}
                          className={cn(
                            'flex items-start gap-3 rounded border p-2.5',
                            overdue
                              ? 'border-warn/40 bg-warn/5'
                              : 'border-border/[0.16] bg-surface-2',
                          )}
                        >
                          <Clock
                            className={cn(
                              'h-3.5 w-3.5 mt-0.5 flex-shrink-0',
                              overdue ? 'text-warn' : 'text-accent-2',
                            )}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2">
                              <span
                                className={cn(
                                  'text-xs font-mono',
                                  overdue ? 'text-warn font-semibold' : 'text-text-dim',
                                )}
                              >
                                {dueLabel}
                              </span>
                              {overdue && (
                                <span className="text-[10px] uppercase tracking-wider text-warn font-semibold">
                                  overdue
                                </span>
                              )}
                              {f.status === 'snoozed' && (
                                <span className="text-[10px] uppercase tracking-wider text-accent-2 font-semibold">
                                  snoozed
                                </span>
                              )}
                            </div>
                            {f.note && (
                              <div className="text-sm text-text mt-0.5 whitespace-pre-wrap">
                                {f.note}
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col gap-1">
                            <button
                              type="button"
                              onClick={() =>
                                void updateFollowUp.mutateAsync({
                                  id: f.id,
                                  patch: { status: 'done' },
                                })
                              }
                              className="rounded border border-success/40 bg-success/10 px-2 py-0.5 text-[10px] font-semibold text-success hover:bg-success/20 transition flex items-center gap-1"
                            >
                              <Check className="h-3 w-3" /> Done
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                void snoozeFollowUp.mutateAsync({ id: f.id, minutes: 60 })
                              }
                              className="rounded border border-border/[0.16] bg-surface-2 px-2 py-0.5 text-[10px] font-medium text-text-dim hover:text-text transition flex items-center gap-1"
                            >
                              <Clock className="h-3 w-3" /> +1h
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>

            <div className="lg:col-span-3">
              <NotesTimeline contactId={contact.id} pinnedNote={contact.notes ?? null} />
            </div>

            <Card className="bg-surface border-border/[0.16] lg:col-span-3">
              <CardHeader>
                <CardTitle className="text-sm">Activity timeline ({activities.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {activities.length === 0 ? (
                  <p className="text-text-mute text-sm">No activity logged yet.</p>
                ) : (
                  <div className="space-y-3">
                    {activities.map((a) => (
                      <div key={a.id} className="flex gap-3 text-sm">
                        <div className="flex-shrink-0 w-20 text-xs text-text-mute font-mono pt-1">
                          {a.created_at ? new Date(a.created_at).toLocaleDateString('en-GB') : '—'}
                        </div>
                        <div className="flex-shrink-0 w-16 text-xs">
                          <span className="px-2 py-0.5 rounded bg-surface-2 border border-border/[0.08]">
                            {a.type ?? '—'}
                          </span>
                        </div>
                        <div className="flex-1 text-text-dim">{a.body ?? '—'}</div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <ScheduleFollowUp
            open={followUpOpen}
            onOpenChange={setFollowUpOpen}
            contactId={contact.id}
            contactName={fullName}
          />
        </div>
      </main>
    </div>
  );
}

